import React, { HTMLProps, useRef, useState } from 'react'
import { Link } from 'gatsby'
import { FormProvider, useForm, useFormContext } from 'react-hook-form'
import ReCAPTCHA from 'react-google-recaptcha'
import { twMerge } from 'tailwind-merge'

import { FormLabel } from './FormLabel'
import { Input } from './Input'
import { Textarea } from './Textarea'

export interface ContactFormValues {
  fullName: string
  phone: string
  email: string
  message?: string
}

interface FormGroupProps extends HTMLProps<HTMLDivElement> {
  children: React.ReactNode
  name: string
  label?: string
  htmlFor?: string
}

const FormGroup = ({
  children,
  name,
  label,
  htmlFor,
  className,
  ...rest
}: FormGroupProps) => {
  const { formState } = useFormContext()
  const { errors } = formState
  const _className = twMerge('grid gap-1 grid-cols-1 content-start', className)

  return (
    <div className={_className} {...rest}>
      {label && <FormLabel htmlFor={htmlFor}>{label}</FormLabel>}{' '}
      {children && children}
      {errors && errors[name] && (
        <p className="text-red-800 text-xs">
          {errors[name]?.message?.toString()}
        </p>
      )}
    </div>
  )
}

export const ContactForm = () => {
  const recaptchaRef = useRef<ReCAPTCHA>(null)
  const [formState, setFormState] = useState<{
    type: 'success' | 'error' | null
    message: string | null
  }>({
    type: null,
    message: null,
  })
  const methods = useForm<ContactFormValues>({
    defaultValues: {},
  })
  const {
    handleSubmit,
    formState: { isSubmitting },
    reset,
    register,
  } = methods

  const handleFormSubmit = async (values: ContactFormValues) => {
    const token = await recaptchaRef?.current?.executeAsync()

    if (!token) {
      return new Promise((resolve, reject) => {
        reject()
        setFormState({
          type: 'error',
          message:
            'Odeslání formuláře selhalo, zkuste nás prosím kontaktovat telefonicky.',
        })
      })
    }

    return new Promise((resolve, reject) => {
      fetch('/.netlify/functions/sendContact', {
        method: 'POST',
        body: JSON.stringify({
          ...values,
        }),
      })
        .then((response) => response.json())
        .then((json) => {
          if (json.success) {
            reset()
            setFormState({
              type: 'success',
              message: 'Děkujeme. Brzy se vám ozveme.',
            })

            setTimeout(() => {
              setFormState({
                type: null,
                message: null,
              })
            }, 5000)

            if (
              typeof window !== 'undefined' &&
              typeof window.gtag !== 'undefined'
            ) {
              window.gtag('event', 'conversion', {
                send_to: 'AW-928052293/C92-CNHKvocYEMXow7oD',
              })
            }

            resolve(true)
          }

          if (json.status === 500) {
            reject()
            setFormState({
              type: 'error',
              message:
                'Odeslání formuláře selhalo, zkuste nás prosím kontaktovat telefonicky.',
            })
          }
        })
        .catch((error) => {
          setFormState({
            type: 'error',
            message:
              'Odeslání formuláře selhalo, zkuste nás prosím kontaktovat telefonicky.',
          })

          reject()
          console.error(error.toString())
        })
    })
  }

  return (
    <FormProvider {...methods}>
      <form method="POST" noValidate onSubmit={handleSubmit(handleFormSubmit)}>
        <div className="grid gap-4">
          <div className="grid gap-1">
            {formState && formState.type === 'success' && (
              <p className="p-2 rounded border border-green-800 bg-green-100 text-green-800">
                {formState.message}
              </p>
            )}
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="grid gap-4">
              <FormGroup name="fullName" label="Celé jméno" htmlFor="fullName">
                <Input
                  id="fullName"
                  placeholder="Vaše jméno a příjmení *"
                  required
                  autoComplete="name"
                  autoCorrect="off"
                  {...register('fullName', {
                    required: 'Zadejte prosím Vaše celé jméno.',
                  })}
                />
              </FormGroup>

              <div className="grid md:grid-cols-2 gap-4">
                <FormGroup
                  name="phone"
                  label="Telefon"
                  htmlFor="phone"
                  className="relative"
                >
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="Váš telefon *"
                    autoComplete="tel"
                    autoCorrect="off"
                    {...register('phone', {
                      required: 'Zadejte prosím Vaše telefonní číslo.',
                      minLength: 9,
                      maxLength: 15,
                      pattern: /[0-9+\ ]{9,15}/,
                      validate: (value) =>
                        value.length >= 9 && value.length <= 15,
                    })}
                    maxLength={15}
                    onKeyDown={(event) => {
                      if (
                        !/[0-9+\ ]/.test(event.key) &&
                        event.key !== 'Backspace'
                      ) {
                        event.preventDefault()
                      }
                    }}
                  />
                </FormGroup>

                <FormGroup name="email" label="E-mail" htmlFor="email">
                  <Input
                    id="email"
                    type="email"
                    placeholder="Váš e-mail *"
                    autoComplete="email"
                    autoCorrect="off"
                    autoCapitalize="off"
                    {...register('email', {
                      required: 'Zadejte prosím Váš e-mail.',
                    })}
                  />
                </FormGroup>
              </div>
            </div>

            <div className="grid gap-4">
              <FormGroup
                name="message"
                label="Poznámka"
                htmlFor="message"
                className="h-full grid-rows-[auto,1fr] items-stretch"
              >
                <Textarea
                  className="h-full"
                  rows={4}
                  placeholder="Napište s čím vám můžeme pomoct"
                  {...register('message')}
                ></Textarea>
              </FormGroup>
            </div>

            <div className="hidden md:block">&nbsp;</div>

            <div className="grid md:grid-cols-[1fr,1fr] gap-x-8 gap-y-4 items-center">
              <p className="text-sm order-1 md:order-0">
                Odesláním souhlasíte se&nbsp;
                <Link
                  className="underline"
                  to="/zasady-zpracovani-osobnich-udaju/"
                >
                  zpracováním&nbsp;osobních&nbsp;údajů
                </Link>
                .
              </p>

              <button
                className="button button--primary h-[42px] md:order-1"
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Odesílám...' : 'Odeslat'}
              </button>
            </div>

            <ReCAPTCHA
              ref={recaptchaRef}
              size="invisible"
              sitekey={process.env.GATSBY_GOOGLE_RECAPTCHA_SITE_KEY || ''}
            />
          </div>
        </div>
      </form>
    </FormProvider>
  )
}
